import { useQueryStates } from 'nuqs';
import { useResolveContactQuery } from '@/hooks/queries/use-resolve-contact-query';
import { contactDrawerParsers } from './contact-drawer-url-state';
import { useContactResolveRef } from './use-contact-resolve-ref';

/**
 * Resolves the drawer's URL entry to the real `creatorIdentityId` that every
 * lazy per-view slice is keyed by.
 *
 * `contactId` is the raw entry id (userId for affiliate, username for creator);
 * the resolve query maps it to the identity id. Returns `null` until the query
 * settles, so views show their loading or empty states instead of fabricating
 * data. Shares the resolve query key with `GlobalContactDrawer`, so no extra
 * request is made.
 */
/**
 * The resolved contact, or null while the drawer is closed or the query has not
 * settled. The two field hooks below share it — and its cache entry, so reading
 * both costs one request.
 */
const useResolvedContact = () => {
  const [{ contactId }] = useQueryStates(contactDrawerParsers);
  const { data: resolved } = useResolveContactQuery(useContactResolveRef());

  return contactId ? (resolved ?? null) : null;
};

export const useResolvedContactIdentityId = (): string | null =>
  useResolvedContact()?.creatorIdentityId ?? null;

/**
 * The drawer's resolved display name, for copy that addresses the contact by
 * name. Shares the same resolve query as the id above, so it costs no request.
 * Null until the query settles: surfaces fall back to impersonal copy rather
 * than greeting nobody.
 */
export const useResolvedContactDisplayName = (): string | null =>
  useResolvedContact()?.displayName ?? null;

/**
 * True while the drawer's entry is on its first resolve. The identity id above
 * reads null then, exactly as it does for a contact that has no identity, so a
 * view that must tell the two apart (loading versus nothing to show) asks here.
 */
export const useIsContactResolving = (): boolean =>
  useResolveContactQuery(useContactResolveRef()).isLoading;
